






















































































import {
    Component,
    Emit,
    Prop,
    PropSync,
    Vue,
    Watch,
} from 'vue-property-decorator';
import Grid from '@/components/event-flow/event-flow-grid/Grid.vue';
import TimelineGrid from '@/components/event-flow/event-flow-grid/TimelineGrid.vue';
import LocationHeader from '@/components/event-flow/event-flow-grid/LocationHeader.vue';
import TopicGrid from '@/components/event-flow/event-flow-grid/TopicGrid.vue';
import GridItem from '@/components/event-flow/event-flow-grid/GridItem.vue';
import { addMinutes, differenceInMinutes } from 'date-fns';
import { differenceInTime, timeFormatOptions } from '@/lib/date-util';
import {
    EventFlowItem,
    EventFlowLocation,
    ExtraFunctionFormData,
} from '@/model/event-flow/EventFlow';
import { setFlow } from '@/lib/helper/event-flow-helper';
import cloneDeep from 'lodash.clonedeep';
import { PropType } from 'vue';
import EventFlowStep from '@/model/enums/event-flow/event-flow-step';
import EventFlowLegend from '@/components/event-flow/EventFlowLegend.vue';
import { Responsible } from '@/model/Responsible';

@Component({
    components: {
        Grid,
        TimelineGrid,
        LocationHeader,
        TopicGrid,
        GridItem,
        EventFlowLegend,
    },
})
export default class EventFlowGrid extends Vue {
    @Prop(Number) readonly step!: number;

    @Prop(Date) readonly startDateTime!: Date;

    @Prop(Date) readonly endDateTime!: Date;

    @Prop(Array) readonly eventFlowItems!: EventFlowItem[];

    @Prop({ default: false, type: Boolean }) readonly isSheet!: boolean;

    @PropSync('extraData', {
        type: Object as PropType<ExtraFunctionFormData>,
        default: () => ({
            showTime: false,
            showBrief: false,
            showLegend: false,
        }),
    })
    readonly extraFunctionFormData!: ExtraFunctionFormData;

    @Prop(Array) readonly responsibles!: Responsible[];

    eventFlow = setFlow(cloneDeep(this.eventFlowItems));

    movedObject: EventFlowItem | null = null;

    created(): void {
        this.$root.$on('click', this.click);
        this.$root.$on('updateItem', this.update);
        this.$root.$on('changeMoveItem', this.changeMoveItem);
        this.$root.$on('itemChangedLocal', this.itemChangedLocal);
    }

    get eventFlowLocations(): EventFlowLocation[] {
        return this.eventFlow?.eventFlowLocations ?? [];
    }

    get timeValues(): string[] {
        const timeValues = [] as string[];
        let sTime = this.startDateTime;

        while (this.endDateTime >= sTime) {
            timeValues.push(this.formatDateToTime(sTime));
            sTime = addMinutes(sTime, this.step);
        }

        return timeValues;
    }

    get lastPos(): number {
        const fullDurationInMinutes = differenceInMinutes(
            this.endDateTime,
            this.startDateTime,
        );

        return Math.round(fullDurationInMinutes / this.step) + 2;
    }

    getMinuteStringFromTimeString(timeString: string): string {
        return timeString?.split(':')[1] ?? '';
    }

    showBorder(timeString: string): boolean {
        if (
            this.step === EventFlowStep.SIXTY_MINUTES ||
            this.step === EventFlowStep.THIRTY_MINUTES
        ) {
            return false;
        }

        const minuteString = this.getMinuteStringFromTimeString(timeString);

        if (
            minuteString === '00' &&
            [
                EventFlowStep.FIFTEEN_MINUTES,
                EventFlowStep.TEN_MINUTES,
                EventFlowStep.FIVE_MINUTES,
                EventFlowStep.ONE_MINUTE,
            ].includes(this.step)
        ) {
            return true;
        }

        if (
            minuteString === '30' &&
            [
                EventFlowStep.TEN_MINUTES,
                EventFlowStep.FIVE_MINUTES,
                EventFlowStep.ONE_MINUTE,
            ].includes(this.step)
        ) {
            return true;
        }

        if (minuteString === '15' && EventFlowStep.ONE_MINUTE === this.step) {
            return true;
        }

        return false;
    }

    @Watch('eventFlowItems')
    eventFlowUpdate(): void {
        this.eventFlow = setFlow(cloneDeep(this.eventFlowItems));
    }

    itemChangedLocal(eventFlowItem: EventFlowItem): void {
        const newLocation = this.eventFlow?.eventFlowLocations.find(
            (location) =>
                location.id ===
                (eventFlowItem?.location?.id === undefined
                    ? null
                    : eventFlowItem?.location?.id),
        );

        const newTopic = newLocation?.eventFlowTopics.find(
            (topic) =>
                topic.id ===
                (eventFlowItem?.topic?.id === undefined
                    ? null
                    : eventFlowItem?.topic?.id),
        );

        if (this.movedObject) {
            this.movedObject.startedAt = eventFlowItem.startedAt;
            this.movedObject.endedAt = eventFlowItem.endedAt;

            if (
                this.movedObject.topic?.id !== eventFlowItem.topic?.id ||
                this.movedObject.location?.id !== eventFlowItem.location?.id
            ) {
                const oldLocation = this.eventFlow?.eventFlowLocations.find(
                    (location) =>
                        location.id ===
                        (this.movedObject?.location?.id === undefined
                            ? null
                            : this.movedObject?.location?.id),
                );

                const oldTopic = oldLocation?.eventFlowTopics.find(
                    (topic) =>
                        topic.id ===
                        (this.movedObject?.topic?.id === undefined
                            ? null
                            : this.movedObject?.topic?.id),
                );

                let oldItems;

                if (oldTopic) {
                    const oldIndex = oldTopic?.items.findIndex(
                        (item) => item.id === eventFlowItem.id,
                    );
                    oldItems = oldTopic.items.splice(oldIndex, 1);
                }

                if (newTopic && oldItems && oldItems.length > 0) {
                    oldItems[0].location = eventFlowItem.location;
                    oldItems[0].topic = eventFlowItem.topic;
                    oldItems[0].startedAt = eventFlowItem.startedAt;
                    oldItems[0].endedAt = eventFlowItem.endedAt;
                    newTopic?.items.push(oldItems[0]);
                }

                this.movedObject.location = eventFlowItem.location;
                this.movedObject.topic = eventFlowItem.topic;
            }
        }

        if (newTopic) {
            const changedItem = newTopic.items.find(
                (item) => item.id === eventFlowItem.id,
            );

            if (changedItem) {
                changedItem.startedAt = eventFlowItem.startedAt;
                changedItem.endedAt = eventFlowItem.endedAt;
            }
            newTopic.items = newTopic?.items.sort((a, b) => {
                if (a.startedAt.getTime() === b.startedAt.getTime()) {
                    return a.endedAt >= b.endedAt ? 1 : -1;
                }

                return a.startedAt > b.startedAt ? 1 : -1;
            });
        }
    }

    @Emit()
    click(item: EventFlowItem): EventFlowItem {
        return item;
    }

    @Emit()
    update(item: EventFlowItem): EventFlowItem {
        if (item.location?.id == null) {
            item.location = undefined;
        }
        if (item.topic?.id == null) {
            item.topic = undefined;
        }
        return item;
    }

    changeMoveItem(eventFlowItem: EventFlowItem | null): void {
        this.movedObject = eventFlowItem;
    }

    createTooltipText(eventFlowItem: EventFlowItem): {
        title: string;
        disabled: boolean;
    } {
        let tooltipText = '';

        if (!this.movedObject) {
            tooltipText += `<b style='font-size:18px'>${this.formatDateToTime(
                eventFlowItem.startedAt,
            )} - ${this.formatDateToTime(eventFlowItem.endedAt)} </b> <br />`;

            if (eventFlowItem.responsible) {
                tooltipText += `<div class="mb-2"><b class='resp-color' style='background-color:${eventFlowItem.responsible.color}'></b>`;
                tooltipText += ` <b>  ${
                    eventFlowItem.responsible?.name ??
                    this.$t('responsible.default')
                }</b></div>`;
            }

            tooltipText += `<b>${this.$t(
                'event-flow.tooltip.total-time',
            )}</b> ${differenceInTime(
                eventFlowItem.endedAt,
                eventFlowItem.startedAt,
            )} <br/>`;

            tooltipText += `<b>${this.$t('event-flow.tooltip.location')}</b> ${
                eventFlowItem.location?.name ?? this.$t('location.default')
            } <br/>`;

            tooltipText += `<b>${this.$t('event-flow.tooltip.topic')}</b> ${
                eventFlowItem.topic?.name ?? this.$t('topic.default')
            } <br/>`;

            if (eventFlowItem.description) {
                tooltipText += ` <b style='margin-top:25px'>${this.$t(
                    'event-flow.tooltip.description',
                )}</b>`;
                tooltipText += `<div class="tooltip-desc">${eventFlowItem.description}</div>`;
            }
        }

        return { title: tooltipText, disabled: !!this.movedObject };
    }

    formatDateToTime(date: Date): string {
        return date.toLocaleTimeString(this.$i18n.locale, timeFormatOptions);
    }
}
